import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Store, TrendingUp, Wallet, CheckCircle2, Clock, ChevronRight } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { ProductCard } from "./ProductsPage";

const field =
  "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20";

// ---------------- Sell With OrvDigital landing + application ----------------
export const SellPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ full_name: "", phone: "", store_name: "", social: "", plan: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) api.get("/partner/status").then((r) => setStatus(r.data)).catch(() => {});
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/partner/apply", form);
      toast.success("Application submitted! We'll review it soon.");
      const r = await api.get("/partner/status");
      setStatus(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    { icon: Store, title: "Your Own Storefront", desc: "Get a dedicated OrvDigital storefront to sell curated products." },
    { icon: TrendingUp, title: "Set Your Prices", desc: "Choose your selling price within approved ranges and keep the margin." },
    { icon: Wallet, title: "Track Earnings", desc: "Transparent earnings, holding periods and payout history." },
  ];

  return (
    <div className="min-h-screen bg-[#050506] pt-24 md:pt-32 pb-16 md:pb-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10 md:mb-16">
          <span className="inline-block px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full text-xs font-medium text-purple-300 uppercase tracking-wider mb-5 md:mb-6">
            Become a Partner
          </span>
          <h1 className="text-3xl md:text-6xl font-bold text-white mb-4 md:mb-6">Build Your Digital Store with OrvDigital</h1>
          <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto">
            Become an approved OrvDigital Partner Seller. Choose from eligible products, set your prices and sell through your own storefront.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6 mb-8 md:mb-12">
          {benefits.map((b, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="flex md:block items-center gap-4 p-4 md:p-6 bg-white/5 border border-white/10 rounded-2xl"
            >
              <div className="p-3 bg-purple-500/10 rounded-xl inline-block md:mb-4 shrink-0">
                <b.icon className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="text-base md:text-lg font-bold text-white md:mb-2">{b.title}</h3>
                <p className="text-sm text-gray-400">{b.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Process steps */}
        <div className="mb-10 md:mb-14">
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
            {["Apply", "Review", "Approval", "Seller Hub", "Start Selling"].map((step, i, arr) => (
              <React.Fragment key={step}>
                <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs md:text-sm text-gray-300">{step}</span>
                {i < arr.length - 1 && <ChevronRight className="w-4 h-4 text-purple-400/70" />}
              </React.Fragment>
            ))}
          </div>
          <p className="text-center text-xs md:text-sm text-gray-500 max-w-2xl mx-auto mt-4">
            Partner applications are reviewed by our team. Product eligibility and selling-price limits are controlled by OrvDigital.
          </p>
        </div>

        {/* Application area */}
        <div className="max-w-xl mx-auto">
          <div className="text-center p-8 md:p-10 bg-white/5 border border-white/10 rounded-2xl">
  <div className="text-5xl mb-4">🚀</div>

  <h3 className="text-2xl font-bold text-white mb-3">
    Seller Hub Coming Soon
  </h3>

  <p className="text-gray-300 max-w-xl mx-auto leading-relaxed">
    We're putting the finishing touches on the OrvDigital Seller Hub to deliver
    the best experience for creators.
  </p>

  <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-purple-600/20 border border-purple-500/30 px-5 py-2">
    <span className="h-2 w-2 rounded-full bg-purple-400 animate-pulse"></span>
    <span className="text-purple-300 font-medium">
      Coming Soon
    </span>
  </div>

  <p className="text-gray-500 text-sm mt-6">
    Thank you for your patience. We're building something worth waiting for.
  </p>
</div>
        </div>
      </div>
    </div>
  );
};

// ---------------- Partner Seller Hub ----------------
export const PartnerHubPage = () => {
  const [overview, setOverview] = useState(null);
  const [products, setProducts] = useState([]);
  const [earnings, setEarnings] = useState({ earnings: [], payouts: [] });
  const [tab, setTab] = useState("overview");

  const load = () => {
    api.get("/partner/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/partner/eligible-products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/partner/earnings").then((r) => setEarnings(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const saveProduct = async (p, price, active) => {
    try {
      await api.post("/partner/products", { product_id: p.id, selling_price: Number(price), active });
      toast.success("Saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const stat = (label, value) => (
    <div className="p-5 bg-white/5 border border-white/10 rounded-2xl">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050506] pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">Seller Hub</h1>
            {overview && (
              <Link to={overview.store_url} className="text-purple-400 text-sm hover:text-purple-300">
                View my store: {overview.store_url}
              </Link>
            )}
          </div>
        </div>

        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {stat("Pending", `₹${overview.pending_earnings}`)}
            {stat("Available", `₹${overview.available_earnings}`)}
            {stat("Paid", `₹${overview.paid_earnings}`)}
            {stat("Total Sales", overview.total_sales)}
          </div>
        )}

        <div className="flex gap-2 mb-8 border-b border-white/10">
          {["overview", "products", "earnings"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-3 text-sm font-medium capitalize border-b-2 transition-all ${
                tab === t ? "border-purple-500 text-white" : "border-transparent text-gray-400 hover:text-white"
              }`}
            >
              {t === "products" ? "Products to Sell" : t}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <div className="space-y-4">
            {products.length === 0 && <p className="text-gray-400">No eligible products available yet.</p>}
            {products.map((p) => (
              <PartnerProductRow key={p.id} product={p} onSave={saveProduct} />
            ))}
          </div>
        )}

        {tab === "earnings" && (
          <div className="space-y-3">
            {earnings.earnings.length === 0 ? (
              <p className="text-gray-400 py-10 text-center">No earnings yet.</p>
            ) : (
              earnings.earnings.map((e) => (
                <div key={e.id} className="flex items-center justify-between p-5 bg-white/5 border border-white/10 rounded-xl">
                  <div>
                    <p className="text-white font-medium">{e.product_name}</p>
                    <p className="text-xs text-gray-500">Sold at ₹{e.selling_price} · base ₹{e.base_price}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-bold">₹{e.amount}</p>
                    <p className="text-xs capitalize text-gray-400">{e.status}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "overview" && overview && (
          <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
            <p className="text-gray-300">
              Welcome to your Seller Hub. Choose products to sell, set your prices within the allowed range, and share your storefront link. Earnings become available after a {" "}
              <span className="text-white font-semibold">7-day holding period</span>, and payouts are processed by the OrvDigital team.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const PartnerProductRow = ({ product, onSave }) => {
  const [price, setPrice] = useState(product.selling_price);
  const [active, setActive] = useState(product.selected);
  return (
    <div className="flex flex-col md:flex-row md:items-center gap-4 p-5 bg-white/5 border border-white/10 rounded-xl">
      <img src={product.cover_image} alt="" className="w-16 h-16 rounded-lg object-cover" />
      <div className="flex-1">
        <p className="text-white font-medium">{product.name}</p>
        <p className="text-xs text-gray-500">Allowed: ₹{product.min_price} – ₹{product.max_price} · base ₹{product.base_price}</p>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <span className="text-gray-400">₹</span>
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="w-24 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-400">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active
        </label>
        <Button onClick={() => onSave(product, price, active)} className="bg-purple-600 text-white">Save</Button>
      </div>
    </div>
  );
};

// ---------------- Public Storefront ----------------
export const StorefrontPage = () => {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/store/${slug}`).then((r) => setStore(r.data)).catch(() => setStore(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading)
    return <div className="min-h-screen bg-[#050506] flex items-center justify-center"><div className="w-10 h-10 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" /></div>;
  if (!store)
    return <div className="min-h-screen bg-[#050506] flex items-center justify-center"><p className="text-gray-400">Store not found.</p></div>;

  return (
    <div className="min-h-screen bg-[#050506] pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <span className="text-purple-400 text-sm">OrvDigital Partner Store</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-2">{store.store_name}</h1>
        </div>
        {store.products.length === 0 ? (
          <p className="text-gray-400">This store has no products listed yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {store.products.map((p, i) => (
              <ProductCard key={p.id} product={{ ...p, licenses: { creator: { price: p.price } } }} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
